// YTBuildAsYouGoTab — Jun 2026 (Randy). See
// /app/memory/FEATURE_BUILD_AS_YOU_GO_SPEC.md.
//
// The hole: in "selected videos" mode, a comment on a video that was never
// processed into the silo is skipped with NO tombstone — invisible to the
// scrubber, reachable only by a blind newest-first walk through channel
// history. Creators who don't want to build a full Twin before switching the
// module on lose the comments that arrive while they're still building.
//
// This tab finds exactly those comments and ANSWERS NOTHING. It groups them by
// video, because the video is the actual call to action: hand it to the
// Workbench, run your macro, and the ordinary Inbox takes over from there.
import { useCallback, useEffect, useState } from "react";
import {
  Shovel, ArrowClockwise, ArrowRight, CheckCircle, WarningCircle, CaretDown,
  CaretRight, Tray, X, LockKey, Plugs,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { ytApi } from "@/lib/ytApi";

const SURFACE = { channel1: "workbench", channel2: "file_refinery" };
const SURFACE_TAB = { channel1: "workbench", channel2: "file-refinery" };
const SURFACE_LABEL = { channel1: "Workbench", channel2: "Refinery" };

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 31) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export default function YTBuildAsYouGoTab({
  siloId, channelSlot = "channel1", isUnlocked, isConnected, onCount,
}) {
  const [groups, setGroups] = useState([]);
  const [gaps, setGaps] = useState(null);
  const [filterMode, setFilterMode] = useState("all");
  const [depth, setDepth] = useState({ from: 0, to: 0 });
  const [reachedEnd, setReachedEnd] = useState(false);
  const [digging, setDigging] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [open, setOpen] = useState({});
  const [gapsOpen, setGapsOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [note, setNote] = useState("");

  const surface = SURFACE[channelSlot] || "workbench";
  const live = isUnlocked && isConnected && !!siloId;

  const apply = useCallback((d) => {
    setGroups(d.groups || []);
    if (d.gaps) setGaps(d.gaps);
    if (d.filter_mode) setFilterMode(d.filter_mode);
    if (typeof d.session_scanned === "number") {
      setDepth({ from: d.session_scanned_from || 0, to: d.session_scanned || 0 });
    }
    if (typeof d.reached_end === "boolean") setReachedEnd(d.reached_end);
    onCount?.((d.groups || []).reduce((n, g) => n + (g.processed ? 0 : g.comment_count), 0));
  }, [onCount]);

  const load = useCallback(async () => {
    if (!live) { setLoaded(true); return; }
    try {
      apply(await ytApi.unprocessedQueue(siloId, channelSlot));
    } catch { /* quiet — the dig button still works */ }
    setLoaded(true);
  }, [live, siloId, channelSlot, apply]);

  useEffect(() => { load(); }, [load]);

  // Jun 2026 (Randy) — "the group I just completed is still at the top showing
  // it still needs processing… the page just needs a complete refresh". It
  // doesn't any more: a mark-processed broadcasts the same event the strike
  // PATCH does, and this tab stays mounted, so it re-reads itself.
  useEffect(() => {
    const onChanged = (ev) => {
      const d = ev?.detail || {};
      if (d.silo_id && d.silo_id !== siloId) return;
      load();
    };
    window.addEventListener("mysilo:yt-strike-changed", onChanged);
    return () => window.removeEventListener("mysilo:yt-strike-changed", onChanged);
  }, [siloId, load]);

  const dig = async (restart = false) => {
    setDigging(true);
    setNote("");
    try {
      const d = await ytApi.digUnprocessed(siloId, channelSlot, { resetCursor: restart });
      apply(d);
      setNote(d.note || "");
      if (d.found) toast.success(`Found ${d.found} missed comment${d.found === 1 ? "" : "s"}.`);
      else if (d.cursor_reset) toast.info("Position reset — dig again from your newest comments.");
      else toast.message(d.reached_end ? "Nothing missed — you're caught up." : "Nothing new on this pass. Dig again to walk deeper.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dig failed.");
    } finally { setDigging(false); }
  };

  const sendToBuilder = (g) => {
    // Hand the URL to whichever YouTube Tool owns this silo + surface, and
    // (when we're living in the Chats tab) switch SiloManage to that tab
    // first. All silo tabs stay mounted, so nothing reloads.
    window.dispatchEvent(new CustomEvent("mysilo:switch-silo-tab", {
      detail: { silo_id: siloId, tab: SURFACE_TAB[channelSlot] || "workbench" },
    }));
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("mysilo:yt-load-url", {
        detail: { silo_id: siloId, surface, url: g.video_url, video_id: g.video_id, title: g.video_title },
      }));
    }, 60);
  };

  const draftGroup = async (g) => {
    setBusyId(g.video_id);
    try {
      const d = await ytApi.queueToInbox(siloId, channelSlot, { video_id: g.video_id });
      apply(d);
      // Oct 2026 (LINK_LIBRARY_AUTOFIX_PROJECT.md · Phase D1) — Randy: *"it
      // processes through the Q's, but when it's done, the inbox is still
      // empty, until you re-cache the site."* `apply()` only updates THIS list.
      // The Inbox console refreshes on this broadcast or on window focus, and
      // focus is throttled to 30s — so hopping straight over showed nothing.
      // The drafts exist; only the console had not been told.
      if (d.drafted) {
        window.dispatchEvent(new CustomEvent("yt-integration-updated", {
          detail: { siloId, slot: channelSlot },
        }));
        toast.success(d.note);
      } else toast.message(d.note);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't hand these to the Inbox.");
    } finally { setBusyId(""); }
  };

  const dismissGroup = async (g) => {
    setBusyId(g.video_id);
    try {
      await ytApi.queueDismiss(siloId, channelSlot, { video_id: g.video_id });
      await load();
      toast.success("Cleared from this list.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear that group.");
    } finally { setBusyId(""); }
  };

  if (!isUnlocked) {
    return (
      <div className="text-center py-10 px-4" data-testid={`bayg-locked-${channelSlot}`}>
        <LockKey size={20} weight="duotone" className="mx-auto text-[#6B6A68] mb-2" />
        <div className="text-[13px] text-[#1C1B1A] font-medium">Build as you go</div>
        <p className="text-[12px] text-[#6B6A68] mt-1 max-w-sm mx-auto">
          Unlock the add-on to find comments landing on videos that aren&apos;t in your silo yet —
          then build the silo one video at a time instead of all at once.
        </p>
      </div>
    );
  }
  if (!isConnected) {
    return (
      <div className="text-center py-10 px-4" data-testid={`bayg-disconnected-${channelSlot}`}>
        <Plugs size={20} weight="duotone" className="mx-auto text-[#6B6A68] mb-2" />
        <div className="text-[13px] text-[#1C1B1A] font-medium">Connect your channel first</div>
        <p className="text-[12px] text-[#6B6A68] mt-1">There&apos;s nothing to dig through until YouTube is linked.</p>
      </div>
    );
  }

  const pending = groups.filter((g) => !g.processed);
  const ready = groups.filter((g) => g.processed);
  return (
    <div className="space-y-4" data-testid={`bayg-tab-${channelSlot}`}>
      <div>
        <div className="text-[13px] font-medium text-[#1C1B1A]">Build as you go</div>
        <p className="text-[12px] text-[#6B6A68] mt-1 leading-relaxed">
          Comments landing on videos that aren&apos;t in your silo yet. Your scrubber skips these on
          purpose — nothing here gets answered. Send the video to your {SURFACE_LABEL[channelSlot]},
          process it, and the comments become answerable. <span className="text-[#4A5D4E]">Digging
          costs no AI spend — looking is free.</span>
        </p>
      </div>

      {gaps?.total > 0 && (
        <div className="bg-[#FDF8EE] border border-[#E8D9B0] rounded-lg overflow-hidden" data-testid={`bayg-gaps-${channelSlot}`}>
          <button
            onClick={() => setGapsOpen((o) => !o)}
            data-testid={`bayg-gaps-toggle-${channelSlot}`}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left hover:bg-[#FBF2E2] transition-colors"
          >
            {gapsOpen
              ? <CaretDown size={13} weight="bold" className="text-[#8A6A2F] shrink-0" />
              : <CaretRight size={13} weight="bold" className="text-[#8A6A2F] shrink-0" />}
            <WarningCircle size={14} weight="duotone" className="text-[#8A6A2F] shrink-0" />
            <span className="text-[11px] uppercase tracking-[0.16em] text-[#8A6A2F] font-medium">Coverage holes</span>
            <span className="flex items-center gap-1.5 ml-auto">
              {gaps.monitored_unprocessed_count > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F6E2D8] text-[#A8502F] font-medium" data-testid={`bayg-gap-pill-unprocessed-${channelSlot}`}>
                  {gaps.monitored_unprocessed_count} answering, never built
                </span>
              )}
              {gaps.excluded_processed_count > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EDE6D4] text-[#6B5A33] font-medium" data-testid={`bayg-gap-pill-excluded-${channelSlot}`}>
                  {gaps.excluded_processed_count} built, not answering
                </span>
              )}
            </span>
          </button>
          {gapsOpen && (
            <div className="px-3 pb-3 pt-0.5 space-y-3 border-t border-[#E8D9B0]/70">
              {gaps.monitored_unprocessed_count > 0 && (
                <div data-testid={`bayg-gap-unprocessed-${channelSlot}`}>
                  <p className="text-[12px] text-[#6B5A33] pt-2">
                    <strong>{gaps.monitored_unprocessed_count}</strong> video{gaps.monitored_unprocessed_count === 1 ? " is" : "s are"} set
                    to be answered but {gaps.monitored_unprocessed_count === 1 ? "was" : "were"} never processed — replies there get
                    built from the rest of your silo and come out generic. Build {gaps.monitored_unprocessed_count === 1 ? "it" : "them"} and
                    that stops.
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {(gaps.monitored_unprocessed_items || []).map((v) => (
                      <li key={v.video_id} className="flex items-center gap-2 text-[11.5px]" data-testid={`bayg-gap-row-${v.video_id}`}>
                        <a href={v.url} target="_blank" rel="noreferrer"
                          className="min-w-0 flex-1 truncate text-[#1C1B1A] hover:underline">
                          {v.title || v.video_id}
                        </a>
                        <button
                          onClick={() => sendToBuilder({ video_id: v.video_id, video_url: v.url, video_title: v.title })}
                          data-testid={`bayg-gap-send-${v.video_id}`}
                          className="shrink-0 text-[10.5px] px-2 py-1 rounded border border-[#1C1B1A]/30 text-[#1C1B1A] hover:bg-[#1C1B1A] hover:text-[#FDFCF8] transition-colors flex items-center gap-1"
                        >
                          Build it <ArrowRight size={10} weight="bold" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {gaps.excluded_processed_count > 0 && (
                <div data-testid={`bayg-gap-excluded-${channelSlot}`}>
                  <p className="text-[12px] text-[#6B5A33] pt-1">
                    <strong>{gaps.excluded_processed_count}</strong> processed video{gaps.excluded_processed_count === 1 ? " is" : "s are"} unchecked
                    in Settings, so {gaps.excluded_processed_count === 1 ? "its" : "their"} comments stay unanswered. Deliberate? Then ignore
                    this. Otherwise tick {gaps.excluded_processed_count === 1 ? "it" : "them"} in Settings → Video monitor
                    (<span className="italic">Match processed</span> does all of them at once).
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {(gaps.excluded_processed_items || []).map((v) => (
                      <li key={v.video_id} className="text-[11.5px]" data-testid={`bayg-gap-row-${v.video_id}`}>
                        <a href={v.url} target="_blank" rel="noreferrer"
                          className="block truncate text-[#1C1B1A] hover:underline">
                          {v.title || v.video_id}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => dig(false)}
          disabled={digging}
          data-testid={`bayg-dig-${channelSlot}`}
          className="text-[12px] px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#333] disabled:opacity-40 flex items-center gap-1.5"
        >
          <Shovel size={13} weight="duotone" />
          {digging ? "Digging…" : "Dig for missed comments"}
        </button>
        <button
          onClick={() => dig(true)}
          disabled={digging}
          data-testid={`bayg-restart-${channelSlot}`}
          title="Walk again from your newest comments"
          className="text-[11.5px] px-2.5 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40 flex items-center gap-1.5"
        >
          <ArrowClockwise size={12} weight="bold" /> Restart from newest
        </button>
        {depth.to > 0 && (
          <span className="text-[10.5px] text-[#6B6A68]" data-testid={`bayg-depth-${channelSlot}`}>
            threads {depth.from.toLocaleString()}–{depth.to.toLocaleString()} deep
          </span>
        )}
        {reachedEnd && (
          <span className="text-[10.5px] text-[#2F6B3A] flex items-center gap-1" data-testid={`bayg-caught-up-${channelSlot}`}>
            <CheckCircle size={12} weight="fill" /> reached the end of your history
          </span>
        )}
        <button
          onClick={load}
          data-testid={`bayg-refresh-${channelSlot}`}
          title="Re-read this list — free, no YouTube quota and no AI spend"
          className="ml-auto text-[10.5px] text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1"
        >
          <ArrowClockwise size={11} weight="bold" /> Refresh
        </button>
      </div>

      {filterMode === "all" && (
        <div className="bg-[#EAF3EC]/60 border border-[#B9D4BE] rounded-md p-3 text-[12px] text-[#2F5133]" data-testid={`bayg-all-mode-${channelSlot}`}>
          You&apos;re answering <strong>all videos</strong> on this channel, so nothing hides from the
          scrubber — this tab stays empty by design. It earns its keep in &quot;only videos I
          select&quot; mode.
        </div>
      )}

      {note && (
        <div className="text-[11.5px] text-[#6B6A68] italic" data-testid={`bayg-note-${channelSlot}`}>{note}</div>
      )}

      {loaded && groups.length === 0 && filterMode !== "all" && (
        <div className="text-center py-8" data-testid={`bayg-empty-${channelSlot}`}>
          <Tray size={18} weight="duotone" className="mx-auto text-[#6B6A68] mb-2" />
          <div className="text-[12.5px] text-[#1C1B1A]">Nothing missed yet</div>
          <p className="text-[11.5px] text-[#6B6A68] mt-1">Run a dig to check for comments on videos you haven&apos;t built in yet.</p>
        </div>
      )}

      {[...ready, ...pending].map((g) => {
        const isOpen = !!open[g.video_id];
        const busy = busyId === g.video_id;
        return (
          <div
            key={g.video_id}
            data-testid={`bayg-group-${g.video_id}`}
            className={`border hairline rounded-lg overflow-hidden ${g.processed ? "bg-[#F6F7F4]" : "bg-white"}`}
          >
            <div className="px-3 py-2.5 flex items-start gap-2">
              <button
                onClick={() => setOpen((o) => ({ ...o, [g.video_id]: !o[g.video_id] }))}
                data-testid={`bayg-expand-${g.video_id}`}
                className="mt-0.5 text-[#6B6A68] hover:text-[#1C1B1A]"
                aria-label={isOpen ? "Collapse comments" : "Expand comments"}
              >
                {isOpen ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] text-[#1C1B1A] leading-snug truncate">
                  {g.video_title || g.video_id}
                </div>
                <div className="text-[10.5px] text-[#6B6A68] mt-0.5 flex items-center flex-wrap gap-x-2">
                  <span>{g.comment_count} comment{g.comment_count === 1 ? "" : "s"}</span>
                  {/* Jun 2026 (Randy) — "it would be nice for users to see the
                      correlation of completion." Comments an ordinary dig has
                      already drafted get skipped by the button, so say how many
                      before he presses it rather than after. */}
                  {g.already_in_inbox > 0 && (
                    <span className="text-[#2F6B3A]" data-testid={`bayg-already-${g.video_id}`}>
                      · {g.already_in_inbox} already in your Inbox
                    </span>
                  )}
                  {g.newest_published_at && <span>· newest {timeAgo(g.newest_published_at)}</span>}
                  {g.processed ? (
                    <span className="text-[#2F6B3A] uppercase tracking-[0.14em] text-[9px] font-medium" data-testid={`bayg-processed-tag-${g.video_id}`}>
                      · processed
                    </span>
                  ) : (
                    <span className="text-[#C16E5A] uppercase tracking-[0.14em] text-[9px] font-medium">· not in silo</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {g.processed ? (
                  <>
                    <button
                      onClick={() => draftGroup(g)}
                      disabled={busy || g.draftable_count === 0}
                      data-testid={`bayg-to-inbox-${g.video_id}`}
                      className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-40 flex items-center gap-1"
                    >
                      <Tray size={11} weight="duotone" /> {busy ? "Working…" : `Draft ${g.draftable_count ?? g.comment_count} in Inbox`}
                    </button>
                    <button
                      onClick={() => dismissGroup(g)}
                      disabled={busy}
                      data-testid={`bayg-dismiss-${g.video_id}`}
                      title="Clear this group"
                      className="p-1.5 rounded text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-40"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => sendToBuilder(g)}
                      data-testid={`bayg-send-${g.video_id}`}
                      className="text-[11px] px-2.5 py-1.5 rounded-md border border-[#1C1B1A] text-[#1C1B1A] hover:bg-[#1C1B1A] hover:text-[#FDFCF8] transition-colors flex items-center gap-1"
                    >
                      Send to {SURFACE_LABEL[channelSlot]} <ArrowRight size={11} weight="bold" />
                    </button>
                    <button
                      onClick={() => dismissGroup(g)}
                      disabled={busy}
                      data-testid={`bayg-dismiss-${g.video_id}`}
                      title="Never mind this video"
                      className="p-1.5 rounded text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-40"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  </>
                )}
              </div>
            </div>
            {isOpen && (
              <div className="border-t hairline bg-[#FDFCF8] px-3 py-2 space-y-2">
                {g.comments.map((c) => (
                  <div key={c.id} className="text-[11.5px]" data-testid={`bayg-comment-${c.comment_id}`}>
                    <span className="text-[#1C1B1A] font-medium">{c.author}</span>
                    <span className="text-[#6B6A68] ml-1.5 text-[10px]">{timeAgo(c.published_at)}</span>
                    <div className="text-[#1C1B1A] whitespace-pre-wrap mt-0.5">{c.text}</div>
                  </div>
                ))}
                {g.processed && (
                  <div className="text-[10.5px] text-[#2F6B3A] pt-1">
                    This video is in your silo now — hand these to the Inbox and they&apos;ll be drafted
                    with your real safety settings.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
